import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import { usePageMeta } from '@/hooks/usePageMeta';
import { testimonials } from '@/mocks/testimonials';
import { stats, site } from '@/mocks/site';
import type { Testimonial } from '@/types/content';

const reviews = testimonials as Testimonial[];

const principles = [
  {
    icon: 'ri-shield-check-line',
    title: '只整理，不编造',
    desc: '我们只做公开信息的梳理与观察，不制造未经证实的传闻，也不使用耸动标题博取点击。',
  },
  {
    icon: 'ri-time-line',
    title: '按时效分层',
    desc: '热点有时效，内容要分层。今日上新追求快，本周推荐追求稳，两者互不替代。',
  },
  {
    icon: 'ri-user-voice-line',
    title: '写给人看',
    desc: '拒绝口水化的堆砌，每篇内容都回答一个具体问题：这条瓜为什么值得花时间。',
  },
  {
    icon: 'ri-refresh-line',
    title: '错了就改',
    desc: '收到纠错会在核实后尽快更新，并在页面底部标注修订说明，不悄悄替换内容。',
  },
];

const philosophy = [
  { step: '01', title: '先定档期', desc: '内容是跟着时间走的，先把节奏理清，再谈信息量。' },
  { step: '02', title: '再做减法', desc: '一天的热点可能有几十条，我们只留下真正值得读的那几条。' },
  { step: '03', title: '最后给视角', desc: '不只告诉你发生了什么，也告诉你它为什么会被讨论。' },
];

export default function About() {
  const schema = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于我们 - 吃瓜福利社',
        url: 'https://coulex.cn/about',
        description: site.intro,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: site.brand,
        url: 'https://coulex.cn/',
        email: site.email,
        description: site.intro,
      },
    ],
    [],
  );

  usePageMeta({
    title: '关于我们 - 吃瓜福利社的定位与内容原则',
    description:
      '吃瓜福利社以「福利吃瓜」为核心，按档期整理娱乐热点与福利内容。了解我们的网站定位、发展理念、内容原则、读者口碑与联系方式。',
    keywords: '福利吃瓜,关于吃瓜福利社,福利吃瓜网站,娱乐热点整理,内容原则',
    path: '/about',
    schema,
  });

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main className="pt-16 md:pt-20">
        <section className="bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-12">
            <Breadcrumbs items={[{ label: '关于我们' }]} />
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-widest text-primary-600 uppercase mb-3">
                  <span className="w-5 h-px bg-primary-400"></span>
                  About Us
                </span>
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
                  把散落的瓜料，
                  <br />
                  按档期理成一条线
                </h1>
                <p className="mt-5 text-sm md:text-base text-foreground-600 leading-8">{site.intro}</p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Link
                    to="/topics"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
                  >
                    逛逛瓜料库
                  </Link>
                  <a
                    href={`mailto:${site.email}`}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-300 text-foreground-800 text-sm font-medium whitespace-nowrap hover:border-primary-300 hover:text-primary-600 transition-colors"
                  >
                    <i className="ri-mail-line" aria-hidden="true"></i>
                    联系我们
                  </a>
                </div>
              </div>
              <div className="w-full h-[260px] md:h-[340px] rounded-lg overflow-hidden bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20editorial%20desk%20scene%20with%20notebooks%2C%20printed%20magazines%20and%20a%20cup%20of%20tea%20in%20soft%20afternoon%20light%2C%20terracotta%20and%20cream%20palette%2C%20calm%20crafted%20atmosphere%2C%20high%20detail%20editorial%20photography&width=1200&height=900&seq=chigua-about-01&orientation=landscape"
                  alt="吃瓜福利社的编辑工作台，摆放着笔记本与刊物"
                  title="关于吃瓜福利社 - 内容整理与档期编排"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {stats.map((s) => (
                <div key={s.label} className="rounded-lg border border-background-200 bg-background-100 p-6">
                  <p className="font-heading text-2xl md:text-3xl font-bold text-primary-600">{s.value}</p>
                  <p className="mt-2 text-xs md:text-sm text-foreground-600">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="pb-14 md:pb-20">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-4">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-tight">
                  网站定位
                </h2>
                <p className="mt-4 text-sm md:text-base text-foreground-600 leading-8">
                  我们不做又一个信息流，而做一份「有节奏的追更清单」。
                </p>
              </div>
              <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-5">
                {philosophy.map((p) => (
                  <div key={p.step} className="rounded-lg border border-background-200 bg-background-50 p-6">
                    <span className="font-heading text-3xl font-bold text-background-300">{p.step}</span>
                    <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{p.title}</h3>
                    <p className="mt-2 text-sm text-foreground-600 leading-7">{p.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-foreground-950">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50">内容原则</h2>
            <p className="mt-3 text-sm md:text-base text-background-300 max-w-2xl leading-7">
              这四条是我们每天更新前的自检清单，也是「福利吃瓜」这个名字背后的承诺。
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {principles.map((p) => (
                <div key={p.title} className="rounded-lg border border-foreground-800 p-6">
                  <span className="w-11 h-11 rounded-lg bg-primary-500/15 text-accent-400 flex items-center justify-center">
                    <i className={`${p.icon} text-xl`} aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-background-50">{p.title}</h3>
                  <p className="mt-2 text-sm text-background-300 leading-7">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">读者口碑</h2>
            <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl leading-7">
              以下评价整理自长期订阅读者的反馈，我们保留了原意，仅做语句顺顺。
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
              {reviews.map((r) => (
                <figure key={r.name} className="rounded-lg border border-background-200 bg-background-100 p-6">
                  <i className="ri-double-quotes-l text-2xl text-primary-300" aria-hidden="true"></i>
                  <blockquote className="mt-2 text-sm text-foreground-700 leading-7">{r.text}</blockquote>
                  <figcaption className="mt-4 pt-4 border-t border-background-200 text-sm">
                    <span className="font-medium text-foreground-950">{r.name}</span>
                    <span className="text-foreground-500"> · {r.role}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100 border-t border-background-200">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">联系方式</h2>
                <p className="mt-4 text-sm md:text-base text-foreground-600 leading-8">
                  内容纠错、选题推荐、转载授权或商务合作，都可以通过下面的方式找到我们。我们通常会在两个工作日内回复。
                </p>
                <ul className="mt-7 space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="w-10 h-10 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
                      <i className="ri-mail-line text-lg" aria-hidden="true"></i>
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">邮件</span>
                      <a
                        href={`mailto:${site.email}`}
                        className="text-sm font-medium text-foreground-950 hover:text-primary-600 transition-colors"
                      >
                        {site.email}
                      </a>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-10 h-10 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center shrink-0">
                      <i className="ri-weibo-line text-lg" aria-hidden="true"></i>
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">社交平台</span>
                      <span className="text-sm font-medium text-foreground-950">{site.weibo}</span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-10 h-10 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center shrink-0">
                      <i className="ri-time-line text-lg" aria-hidden="true"></i>
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">回复时间</span>
                      <span className="text-sm font-medium text-foreground-950">工作日 10:00 - 19:00</span>
                    </span>
                  </li>
                </ul>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-7 md:p-9">
                <h3 className="font-heading text-xl font-bold text-foreground-950">想第一时间收到更新？</h3>
                <p className="mt-3 text-sm text-foreground-600 leading-7">
                  在首页底部订阅「档期更新」，我们只在上新与换挡时发送提醒，不会频繁打扰。
                </p>
                <Link
                  to="/#subscribe"
                  className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
                >
                  <i className="ri-notification-3-line" aria-hidden="true"></i>
                  去订阅档期提醒
                </Link>
                <div className="mt-7 pt-6 border-t border-background-200">
                  <Link
                    to="/faq"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                  >
                    查看常见问题
                    <i className="ri-arrow-right-line" aria-hidden="true"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}